import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";

const EASE = [0.22, 1, 0.36, 1];

const CASE_STUDIES = {
  "echo-truth": {
    title: "Echo Truth",
    subtitle: "Machine-learning fake news detection",
    tags: ["Python", "NLP", "Machine Learning", "Kaggle"],
    overview:
      "Echo Truth is a machine-learning fake news detector. It scrutinizes news content and verifies its authenticity. In a landscape where misinformation can sway public opinion in hours, the goal was a dependable model that helps readers navigate media with confidence and discernment.",
    approach: [
      "Assembled a comprehensive labeled news dataset from Kaggle, balancing real and fabricated sources.",
      "Cleaned and vectorized article text: tokenization, stop-word handling, and TF-IDF feature extraction.",
      "Trained and compared multiple classifiers, tuning for precision so genuine reporting is never casually flagged.",
      "Evaluated on held-out data, then ran error analysis on the hardest misclassified examples to guide iteration.",
    ],
    pipelineTitle: "Pipeline: how it actually works",
    pipeline: [
      { title: "Kaggle news dataset", desc: "Labeled real and fabricated articles, balanced for training." },
      { title: "Clean & vectorize", desc: "Tokenization, stop-word handling, and TF-IDF feature extraction." },
      { title: "Train classifiers", desc: "Multiple models trained and compared, tuned for precision." },
      { title: "Evaluate & analyze errors", desc: "Held-out evaluation plus review of the hardest misclassifications." },
    ],
    results: [
      "Error analysis showed opinion pieces sit on the hardest decision boundary, flagged for future work.",
    ],
    cta: { label: "View the repository", href: "https://github.com/Sarronnn/EchoTruth" },
  },
  "kis": {
    title: "Kis: A New Coding Language",
    subtitle: "A statically-typed programming language, designed from scratch",
    tags: ["Language Design", "Compilers", "JavaScript"],
    overview:
      "Kis is a statically-typed programming language built from the ground up: its own keyword set, a module system, and operator overloading. The goal was to design a language with personality that never gives up rigor: playful on the surface, disciplined underneath.",
    approach: [
      "Designed the language specification: syntax, type rules, and a keyword set with personality.",
      "Built the lexer and parser to turn raw source code into a structured syntax tree.",
      "Implemented static type checking, operator overloading (addOrConcat), and a full module system.",
      "Wrote example programs and stage-by-stage tests to prove the pipeline end-to-end.",
    ],
    chartTitle: "Compiler stage test pass rate (%)",
    chartNote: "Illustrative summary. Full test details in the repository.",
    chart: [
      { name: "Lexer", value: 100 },
      { name: "Parser", value: 98 },
      { name: "Type Checker", value: 95 },
      { name: "Codegen", value: 90 },
    ],
    results: [
      "A working pipeline from source text to typed, validated programs.",
      "Operator overloading and modules make the language expressive despite its playful surface.",
      "Built collaboratively in a shared Git codebase with code review throughout.",
    ],
    cta: { label: "View the repository", href: "https://github.com/jmendo15/Kis" },
  },
  "rag-assistant": {
    title: "RAG Maintenance Assistant",
    subtitle: "Offline retrieval-augmented AI for transit maintenance",
    tags: ["Python", "FAISS", "Local LLMs", "Streamlit", "BART"],
    overview:
      "Built at BART, this fully offline retrieval-augmented assistant indexes five years of work orders and technical manuals. Technicians describe a problem in plain language and get grounded, source-backed fixes from prior work, with no sensitive operational data ever leaving the building.",
    approach: [
      "Ingested five years of work orders and technical manuals into a chunked, searchable corpus.",
      "Generated embeddings and built a FAISS similarity index for fast semantic retrieval.",
      "Ran local open-source LLMs for generation. The entire stack runs on-premises, fully offline.",
      "Built the Streamlit interface and validated recommendation quality directly with maintenance staff.",
    ],
    results: [
      "Technicians receive source-backed historical fixes instead of searching through manuals or asking engineers.",
      "Used cosine similarity to surface similar past work orders as technicians type in a new issue.",
      "The prototype was designed to run fully offline, so no data is sent over APIs to external language models.",
    ],
  },
};

export default function CaseStudy() {
  const { slug } = useParams();
  const cs = CASE_STUDIES[slug];

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [slug]);

  if (!cs) {
    return (
      <div className="min-h-screen flex flex-col items-start justify-center max-w-7xl mx-auto px-6 md:px-12" data-testid="case-study-not-found">
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-lime mb-6">404</p>
        <h1 className="font-display text-4xl text-cream mb-8">Case study not found.</h1>
        <Link to="/" data-testid="case-study-back-link" className="font-mono text-xs uppercase tracking-[0.25em] text-lime border border-lime/50 px-6 py-3 hover:bg-lime hover:text-forest transition-colors duration-300">
          Back to portfolio
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-32" data-testid="case-study-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
          <Link
            to="/"
            data-testid="case-study-back-link"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-sage hover:text-lime transition-colors duration-300"
          >
            <ArrowLeft size={14} /> Back to portfolio
          </Link>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
          className="mt-16 font-mono text-xs uppercase tracking-[0.35em] text-lime"
        >
          [ Case study ]
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight text-cream"
          data-testid="case-study-title"
        >
          {cs.title}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-4 font-mono text-sm md:text-base text-sage"
        >
          {cs.subtitle}
        </motion.p>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-8 flex flex-wrap gap-3"
        >
          {cs.tags.map((t) => (
            <span key={t} className="font-mono text-[10px] uppercase tracking-[0.2em] border border-treeline text-sage px-3 py-1.5">
              {t}
            </span>
          ))}
        </motion.div>

        <div className="mt-20 grid grid-cols-12 gap-12 md:gap-20">
          <motion.section
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE }}
            className="col-span-12 md:col-span-5"
            data-testid="case-study-overview"
          >
            <h2 className="font-mono text-xs uppercase tracking-[0.35em] text-lime mb-6">Overview</h2>
            <p className="font-mono text-sm md:text-base leading-relaxed text-sage">{cs.overview}</p>

            <h2 className="mt-14 font-mono text-xs uppercase tracking-[0.35em] text-lime mb-6">Approach</h2>
            <ol className="space-y-5">
              {cs.approach.map((step, i) => (
                <li key={i} className="flex gap-4 font-mono text-sm leading-relaxed text-sage">
                  <span className="text-lime shrink-0">0{i + 1}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="col-span-12 md:col-span-7"
            data-testid="case-study-results"
          >
            <h2 className="font-mono text-xs uppercase tracking-[0.35em] text-lime mb-6">Results</h2>
            {cs.pipeline && (
            <div className="border border-treeline bg-pine/40 p-6 md:p-8" data-testid="case-study-pipeline">
              <p className="font-mono text-xs text-sage mb-6">{cs.pipelineTitle}</p>
              <div className="flex flex-col">
                {cs.pipeline.map((step, i) => (
                  <div key={i} className="flex items-stretch gap-4">
                    <div className="flex flex-col items-center">
                      <span className="font-mono text-[10px] text-lime border border-lime/50 px-2 py-1">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {i < cs.pipeline.length - 1 && <span className="w-px flex-1 bg-treeline" />}
                    </div>
                    <div className="pb-6 last:pb-0">
                      <p className="font-mono text-sm text-cream">{step.title}</p>
                      <p className="font-mono text-xs text-sage mt-1 leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            )}
            {cs.chart && (
            <div className="border border-treeline bg-pine/40 p-6 md:p-8">
              <p className="font-mono text-xs text-sage mb-6">{cs.chartTitle}</p>
              <div className="h-64" data-testid="case-study-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={cs.chart} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                    <XAxis dataKey="name" tick={{ fill: "rgb(var(--c-sage))", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "rgb(var(--c-treeline))" }} tickLine={false} />
                    <YAxis domain={[0, 100]} tick={{ fill: "rgb(var(--c-sage))", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                    <Tooltip
                      cursor={{ fill: "rgb(var(--c-moss) / 0.4)" }}
                      contentStyle={{ background: "rgb(var(--c-pine))", border: "1px solid rgb(var(--c-treeline))", fontFamily: "JetBrains Mono", fontSize: 12, color: "rgb(var(--c-cream))" }}
                    />
                    <Bar dataKey="value" radius={0}>
                      {cs.chart.map((_, i) => (
                        <Cell key={i} fill={i === cs.chart.length - 1 ? "rgb(var(--c-lime))" : "rgb(var(--c-moss))"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-sage/70">{cs.chartNote}</p>
            </div>
            )}

            <ul className="mt-10 space-y-4">
              {cs.results.map((r, i) => (
                <li key={i} className="flex gap-3 font-mono text-sm leading-relaxed text-sage">
                  <CheckCircle2 size={16} className="text-lime shrink-0 mt-0.5" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>

            {cs.cta && (
            <a
              data-testid="case-study-repo-button"
              href={cs.cta.href}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-12 inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] bg-lime text-forest px-8 py-4 hover:bg-limedeep transition-colors duration-300"
            >
              {cs.cta.label} <ArrowUpRight size={15} />
            </a>
            )}
          </motion.section>
        </div>
      </div>
    </div>
  );
}
